<template>
  <div>
    <CollapsibleSection
      :header-label="$t('deliveryDetailsPassport')"
      :initial-expanded-state="true"
      disabled
      accent-color="bg-brand-grayblue-300"
      border-color="border-brand-grayblue-300"
      body-class="!p-0"
    >
      <template #header-end>
        <button
          :class="[formIsShowing ? 'bg-brand-gray-600' : 'bg-brand-green hover:bg-brand-green']"
          class="w-40 text-center rounded-md pe-2 flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
          @click.stop.prevent="
            () => {
              isReadOnly = false
              selectedAddress = null
              toggleNewForm()
            }
          "
        >
          <div :class="[formIsShowing ? '' : 'bg-brand-green']" class="p-2 rounded-s-md">
            <faTimes v-if="formIsShowing" class="w-3.5 fill-white"></faTimes>
            <faPlus v-else class="w-4 fill-white"></faPlus>
          </div>
          <span v-if="formIsShowing" class="w-full text-center text-xs ltr:font-sans">
            {{ $t('Cancel') }}
          </span>
          <span v-else class="w-full text-center text-xs ltr:font-sans">
            {{ $t('addNewAddress') }}
          </span>
        </button>
      </template>

      <div class="flex flex-col gap-y-4">
        <DataTable
          v-if="!isLoading"
          :default-headers="[
            'setDefault',
            'emirate',
            'city',
            'area',
            'watermarkMobile',
            'actualAddressDetails',
            'actions'
          ]"
          :data-set="dataSet"
          empty-message="noAddressesAvailable"
        >
          <template #cell="{ columnName, rowIndex }">
            <div v-if="columnName == 'setDefault'" class="w-full h-full flex gap-1">
              <div class="w-full flex justify-center">
                <button
                  :class="[
                    addresses.collection[rowIndex].isDefaultAddressFlag
                      ? 'border-primary'
                      : 'border-brand-gray-600'
                  ]"
                  class="flex justify-center items-center border rounded-full size-3.5"
                  @click="setDefaultAddress(addresses.collection[rowIndex])"
                >
                  <div
                    :class="[
                      addresses.collection[rowIndex].isDefaultAddressFlag ? 'bg-primary' : ''
                    ]"
                    class="rounded-full size-2"
                  ></div>
                </button>
              </div>
            </div>
            <div v-if="columnName == 'actions'" class="w-full h-full flex justify-center gap-1">
              <button
                v-tippy="{ content: $t('View') }"
                class="rounded p-2 bg-brand-green"
                @click="onEditAddress(addresses.collection[rowIndex], true)"
              >
                <faEye class="w-3 fill-white" />
              </button>
              <button
                v-tippy="{ content: $t('Edit') }"
                class="rounded p-2 bg-cyan-400"
                @click="onEditAddress(addresses.collection[rowIndex])"
              >
                <faPenToSquare class="w-3 fill-white" />
              </button>
              <component
                :is="addresses.collection[rowIndex].isDefaultAddressFlag ? 'div' : 'tippy'"
                :theme="useThemeStore().currentScheme == 'light' ? 'light-border' : 'dark-border'"
                :offset="[0, 4]"
                interactive
                trigger="click"
                placement="top"
              >
                <button
                  v-tippy="{
                    content: addresses.collection[rowIndex].isDefaultAddressFlag
                      ? $t('notAllowedToDeleteDefaultAddress')
                      : $t('Delete')
                  }"
                  :class="
                    addresses.collection[rowIndex].isDefaultAddressFlag
                      ? 'bg-brand-gray-600 cursor-not-allowed'
                      : 'bg-brand-red'
                  "
                  class="rounded p-2"
                >
                  <faTrash class="w-3 fill-white" />
                </button>
                <template #content="{ hide }">
                  <div class="w-80 flex flex-col gap-y-2">
                    <h1 class="p-2 ltr:font-sans text-start text-lg border-b border-brand-gray-300">
                      {{ $t('Confirmation') }}
                    </h1>
                    <p class="p-3 text-start text-sm">{{ $t('deleteAddressConfirmationMSG') }}</p>
                    <div class="p-3 w-full flex justify-between">
                      <button
                        class="text-center rounded-md bg-brand-gray-600/80 hover:bg-brand-gray-600 flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
                        @click="hide()"
                      >
                        <div class="p-2 bg-brand-gray-600 rounded-s-md">
                          <faTimes class="w-4 fill-white"></faTimes>
                        </div>
                        <span class="w-full text-center">
                          {{ $t('Cancel') }}
                        </span>
                        <div class="p-0.5"></div>
                      </button>
                      <button
                        class="text-center rounded-md bg-primary/80 hover:bg-primary flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
                        @click="
                          () => {
                            onConfirmDeletion(addresses.collection[rowIndex])
                            hide()
                          }
                        "
                      >
                        <div class="p-2 bg-primary rounded-s-md">
                          <faCheck class="w-4 fill-white"></faCheck>
                        </div>
                        <span class="w-full text-center">
                          {{ $t('Confirm') }}
                        </span>
                        <div class="p-0.5"></div>
                      </button>
                    </div>
                  </div>
                </template>
              </component>
            </div>
          </template>
        </DataTable>

        <template v-if="formIsShowing">
          <NewDeliveryAddress
            :key="selectedAddress"
            :disabled-fields="['deliveryMethodId', 'deliveryCompanyId']"
            :initial-values="
              selectedAddress || {
                deliveryMethodId: 2,
                deliveryCompanyId: 2,
                mobileNumber: '00971',
                phoneNumber: '00971',
                poBoxFlag: 0
              }
            "
            :is-read-only="isReadOnly"
            class="px-4 pb-2"
            @on-cancel="toggleNewForm"
            @on-submit-new-address="submitAddress"
          />
        </template>
      </div>
    </CollapsibleSection>
  </div>
</template>

<script setup>
import faCheck from '@/assets/icons/faCheck.svg?component'
import faEye from '@/assets/icons/faEye.svg?component'
import faPenToSquare from '@/assets/icons/faPenToSquare.svg'
import faPlus from '@/assets/icons/faPlus.svg?component'
import faTimes from '@/assets/icons/faTimes.svg'
import faTrash from '@/assets/icons/faTrash.svg'
import client_lookups from '@/assets/json/clientLookups.json'
import DataTable from '@/components/DataTable/index.vue'
import CollapsibleSection from '@/components/Ui/CollapsibleSection.vue'
import axiosInstance from '@/services'
import { useAuthStore } from '@/stores/auth'
import { useIndAddressMgmtStore } from '@/stores/ind_address_mgmt.js'
import { useLookupsStore } from '@/stores/lookups'
import { useThemeStore } from '@/stores/theme'
import NewDeliveryAddress from '@/views/auth-user/addresses/Modal/NewDeliveryAddress/index.vue'
import { useToggle } from '@vueuse/core'
import { parsePhoneNumber } from 'libphonenumber-js'
import { computed, nextTick, onMounted, reactive, ref, watch } from 'vue'

const TABLE_KEY = 'deliveryAddressesPassport'

const addressMgmtStore = useIndAddressMgmtStore()
const lookupsStore = useLookupsStore()
const authStore = useAuthStore()

const [formIsShowing, toggleNewForm] = useToggle()

const selectedAddress = ref(null)
const isLoading = ref(true)
const isReadOnly = ref(false)
const paginatorConfig = reactive({
  totalItemCount: 0,
  itemsPerPage: 5,
  currentPage: 1,
  pageCount: 0
})

const addresses = computed(() => addressMgmtStore[TABLE_KEY])
const defaultAddress = computed(() =>
  addresses.value.collection.find((a) => a.isDefaultAddressFlag)
)

const dataSet = computed(() => {
  if ((addresses.value?.collection ?? []).length === 0) return null

  return addresses.value.collection.map((c) => {
    const emirate = addressMgmtStore.emirates.find((e) => c.emirateId == e.id)
    const city = addressMgmtStore.emirateCities[c.emirateId].cities.find((t) => t.id == c.cityId)
    const area = addressMgmtStore.cityZones.find((cz) => cz.id == c.cityZoneId)
    const mobileNumberPretty = parsePhoneNumber(c.mobileNumber.replaceAll('00', '+'))
    return {
      setDefault: '',
      emirate: emirate?.text || emirate?.localizedName || '--',
      city: city?.text || city?.localizedName || '--',
      area: area?.text || area?.localizedName || '--',
      watermarkMobile: mobileNumberPretty.number,
      actualAddressDetails: c.addressDetails,
      actions: ''
    }
  })
})

onMounted(async () => {
  await nextTick()

  await addressMgmtStore.fetchLookups()
  await fetchDeliveryCompanies()

  await fetchAddressList()
})

async function fetchAddressList() {
  await addressMgmtStore.fetchAddresses(
    {
      IsPassportDeliveryAddress: true,
      LookupUserAddressTypeId: client_lookups.addressCategory.deliveryPassport,
      pageSize: paginatorConfig.itemsPerPage,
      currentPage: paginatorConfig.currentPage
    },
    TABLE_KEY
  )

  const { numberOfRecords } = addresses.value

  paginatorConfig.totalItemCount = numberOfRecords
  paginatorConfig.pageCount = Math.ceil((numberOfRecords || 1) / paginatorConfig.itemsPerPage)

  isLoading.value = false
}

async function fetchDeliveryCompanies() {
  const response = await lookupsStore.fetchLookup('/api/lookup/DeliveryCompanies')

  const { userAdminRegionId } = authStore.user

  const deliveryCompanyIds = userAdminRegionId == 27 ? [3, 4] : [1, 2]
  addressMgmtStore.deliveryCompanies = response.filter((x) => deliveryCompanyIds.includes(x.id))
}

async function setDefaultAddress(address) {
  const { id: addressId } = address
  /**
   * checks if the clicked address is already the default address
   */
  if (addressId == defaultAddress.value?.id) return

  await axiosInstance.get(`/api/addressesBook/management/setDefault/${addressId}`)
  await fetchAddressList()
}

async function submitAddress(form) {
  const body = {
    ...form,
    isPassportDeliveryAddress: true,
    userAddressTypeId: client_lookups.addressCategory.deliveryPassport
  }
  let path = '/api/addressesBook/management/add'

  if (selectedAddress.value) {
    path = '/api/addressesBook/management/update'
  }

  await axiosInstance.post(path, body)
  await fetchAddressList()
  toggleNewForm()
  selectedAddress.value = null
}

async function onConfirmDeletion(address) {
  await addressMgmtStore.deleteAddress(1, address)
  await fetchAddressList()
}

function onEditAddress(address, disableEdit = false) {
  toggleNewForm()
  selectedAddress.value = address
  isReadOnly.value = disableEdit
}

function resetState() {
  toggleNewForm(false)
  selectedAddress.value = null
  isReadOnly.value = false
}

watch(formIsShowing, () => {
  if (!formIsShowing) {
    resetState()
  }
})
</script>

<script>
export default { name: 'LocalAddresses' }
</script>

<style lang="scss" scoped></style>
